import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Check,
  Link03,
  Mail01,
  MessageTextCircle02,
} from "untitledui-js/react";
import { twMerge } from "tailwind-merge";
import { Button } from "@pondigitalsolutions/pon-component-library";
import { useConfigContext } from "@hooks/useConfigContext";
import { DropdownMenu, DropdownMenuItem } from "./DropdownMenu";

interface ShareButtonProps {
  variant?: "soft" | "primary" | "secondary";
  tabIndex?: number;
  text?: string;
  className?: string;
}

export const ShareButton = ({
  variant = "secondary",
  tabIndex = 0,
  text,
  className = "",
}: ShareButtonProps) => {
  const { t } = useTranslation();
  const { shareRef, state, dispatch } = useConfigContext();
  const { isShareOpen } = state;

  // Local state
  const [linkCopied, setLinkCopied] = useState(false);

  // effects
  useEffect(() => {
    if (linkCopied) {
      const timer = setTimeout(() => setLinkCopied(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [linkCopied]);

  return (
    <div ref={shareRef} className="relative">
      <Button
        size="md"
        variant={variant}
        id="share-button"
        startIcon="Share01"
        className={twMerge(
          "pointer-events-auto aspect-square cursor-pointer border",
          text ? "gap-1" : "h-10 w-10 rounded-full p-0 [&>svg]:h-5 [&>svg]:w-5",
          variant !== "soft" &&
            "dark:border-white/20 dark:bg-white/20 [&>svg]:stroke-secondary-800 dark:[&>svg]:stroke-white",
          className
        )}
        onClick={() =>
          dispatch({
            type: "SHARE_VISIBILITY",
            payload: { visible: !isShareOpen },
          })
        }
        ariaLabel={t("Share.shareButton")}
        tabIndex={tabIndex}
      >
        {text ?? ""}
      </Button>
      <DropdownMenu
        isOpen={isShareOpen}
        id="share-dropdown"
        data-testid="share-dropdown"
      >
        <DropdownMenuItem key="share-email">
          <Mail01 width={20} />
          <a
            href={`mailto:?body=${window.location.href}`}
            className="pointer-events-auto flex"
          >
            {t("Share.shareEmail")}
          </a>
        </DropdownMenuItem>
        <DropdownMenuItem key="share-whatsapp">
          <MessageTextCircle02 width={20} />
          <a
            href={`whatsapp://send?text=${window.location.href}`}
            className="pointer-events-auto flex"
          >
            {t("Share.shareWhatsApp")}
          </a>
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => {
            void navigator.clipboard.writeText(window.location.href);
            setLinkCopied(true);
          }}
          key="share-copy-link"
          className="pointer-events-auto cursor-copy"
        >
          {linkCopied ? <Check width={20} /> : <Link03 width={20} />}
          {linkCopied ? t("Share.linkCopied") : t("Share.shareCopyLink")}
        </DropdownMenuItem>
      </DropdownMenu>
    </div>
  );
};
